import { Highlighter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { FileHighlightFields } from '@/components/documents/FileHighlightFields';
import type { HighlightTagColorSource } from '@/lib/highlightTagColors';
import {
  highlightColorCssVar,
  quotePreview,
  type FileHighlightDraft,
  type HighlightColor,
  type HighlightVisibility,
} from '@/utils/fileHighlights';

interface FileHighlightToolbarProps {
  draft: FileHighlightDraft | null;
  multiPageHint: boolean;
  color: HighlightColor;
  visibility: HighlightVisibility;
  comment: string;
  tags?: readonly HighlightTagColorSource[];
  selectedTagIds?: readonly string[];
  saving?: boolean;
  error?: string | null;
  onColor: (color: HighlightColor) => void;
  onSelectedTagIds?: (ids: string[]) => void;
  onVisibility: (visibility: HighlightVisibility) => void;
  onComment: (comment: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

export function FileHighlightToolbar({
  draft,
  multiPageHint,
  color,
  visibility,
  comment,
  tags = [],
  selectedTagIds = [],
  saving = false,
  error = null,
  onColor,
  onSelectedTagIds,
  onVisibility,
  onComment,
  onSave,
  onCancel,
}: FileHighlightToolbarProps) {
  const { t } = useTranslation();

  if (!draft) {
    return (
      <section className="fp-composer">
        <div className="fp-hint">
          <Highlighter className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            {multiPageHint
              ? t('documents.highlights.multiPageHint')
              : t('documents.highlights.selectHint')}
          </span>
        </div>
      </section>
    );
  }

  return (
    <section className="fp-composer">
      <div className="fp-composer-head">
        <h2>{t('documents.highlights.newTitle')}</h2>
      </div>
      <p className="fp-quote" style={{ ['--hc' as string]: highlightColorCssVar(color) }}>
        {quotePreview(draft.quote)}
      </p>
      <FileHighlightFields
        commentId="fp-new-comment"
        color={color}
        visibility={visibility}
        comment={comment}
        disabled={saving}
        tags={tags}
        selectedTagIds={selectedTagIds}
        onColor={onColor}
        onSelectedTagIds={onSelectedTagIds}
        onVisibility={onVisibility}
        onComment={onComment}
      />
      <div className="fp-composer-actions">
        <Button type="button" variant="secondary" size="sm" onClick={onCancel} disabled={saving}>
          {t('common.cancel')}
        </Button>
        <Button type="button" variant="primary" size="sm" loading={saving} onClick={onSave}>
          {t('documents.highlights.saveHighlight')}
        </Button>
      </div>
      {error ? <p className="fp-form-error">{error}</p> : null}
    </section>
  );
}
